/**
 * Router that will be either browser or static-based depending on whether we
 * are server-side rendering.
 */
import * as React from "react";

import {RequestInfo} from "@khan/shared-core";

import BrowserRouter from "./browser-router.tsx";
import PeriodicUpdateContainer from "./periodic-update-container.tsx";

type Props = {
    /**
     * The component children.
     */
    children: React.ReactNode;
};

/**
 * This is a staticContext object which is passed in to StaticRouter
 * during SSR. We use this to be able to access the url that's set by React
 * Router during SSR, if it hits a <Redirect>. More information here:
 * https://reacttraining.com/react-router/web/api/StaticRouter
 */
export const staticContext: {
    url?: string;
} = {};

/**
 * A router component that renders a StaticRouter or a BrowserRouter depending
 * on whether we're running in server-side rendering or not.
 *
 * When rendering something on the server, we need to use StaticRouter.
 * React router officially supports rehydrating something rendered with
 * StaticRouter using BrowserRouter.
 *
 * @export
 * @class Router
 * @extends {React.Component<Props>}
 */
export default class Router extends React.Component<Props> {
    render(): React.ReactNode {
        const {children} = this.props;

        // NOTE(jared): In the mobile app embedded context, we need all navigation
        // to be normal url navigation (not client-side pushState nav), so that we
        // can intercept navigation requests in react-native and either kick out to
        // the browser (for pages that don't have a mobile native counterpart)
        // or navigate within the mobile app.
        // This does mean that any usages of `withPeriodicUpdateControls` or
        // use `usePeriodicUpdate` will be no-ops. This is OK because we only have
        // a few specific pages that we are embedding in the mobile app.
        const alwaysForceRefresh = RequestInfo.isMobileApp;

        // On the dev server we don't want to use the Periodic Update
        // component as it won't work as expected (it's really meant for a
        // production environment)
        if (RequestInfo.IS_DEV_SERVER) {
            return (
                <BrowserRouter forceRefresh={alwaysForceRefresh}>
                    {children}
                </BrowserRouter>
            );
        }

        return (
            <PeriodicUpdateContainer>
                {({forceRefresh}) => (
                    <BrowserRouter
                        forceRefresh={alwaysForceRefresh || forceRefresh}
                    >
                        {children}
                    </BrowserRouter>
                )}
            </PeriodicUpdateContainer>
        );
    }
}
